import React, { useEffect, useRef, useState } from "react";
import { Item, ComboBox } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import { ListJournalRequest } from "../../../../../../services/soap/finance/requests/ListJournalRequest";
import { ListUserRequest } from "../../../../../../services/soap/access/requests/ListUserRequest";
import { GetContactListRequest } from "../../../../../../services/soap/main/requests/GetContactListRequest";
interface Contact {
    id: string;
    name: string;
}
interface IContactDropDownProps {
    selectedContact: string;              // parent selected id
    contactName?: string;
    onChange: (data: any | null) => void; // null means cleared
    isEdit: any;
    isRequired?: any;
    errorMessage?: any;
    validationState?: any;
    label?: any;
    width?: any;
}

const ContactDropDown: React.FC<IContactDropDownProps> = ({
    selectedContact,
    contactName,
    onChange,
    isEdit,
    isRequired,
    errorMessage,
    validationState,
    label,
    width
}) => {
    const { store, mainService } = useDependency();
    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });
    const resolvedLabel = label ?? _t("contact");

    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState<string>("");

    const didInitialAutoSelectRef = useRef(false);
    const isUserEditingRef = useRef(false);
    const totalContactListRec = useRef<number>(0);
    const offSetLimit = useRef<number>(0);
    const PAGE_SIZE = 50;

    const getContactListCount = async () => {
        const response = await mainService.getContactList(new GetContactListRequest(store.Server, store.SessionId, { countOnly: '1' }));
        if (response.result === 'OK') {
            totalContactListRec.current = response.count != 0 ? response.count : 0;
            return response.count != 0 ? response.count : 0;
        } else {
            return 0;
        }
    };

    const loadcontactList = async () => {
        try {
            const request = new GetContactListRequest(store.Server, store.SessionId, {
                countOnly: "0",
                offset: offSetLimit.current,
                limit: PAGE_SIZE,
                ...(contactName && contactName.trim() !== "" && { contains: `*${contactName}` })
            });
            const response = await mainService.getContactList(request);
            if (response.result === "OK") {
                const items: any[] = response?.CONTACT.map((contact: any) => ({
                    id: contact.id,
                    name: `${contact.name3} ${contact.name1}`,
                }));
                return items;
            }
            return [];
        } catch (e) {
            console.error("Error fetching ContactList:", e);
            return [];
        }
    };

    const list = useAsyncList<Contact>({
        async load({ filterText, cursor }) {
            try {

                let totalRecords = await getContactListCount();
                totalRecords = totalRecords ? Number(totalRecords) : 0;
                let currentPage = cursor ? parseInt(cursor) : 0;
                let offSet = PAGE_SIZE * currentPage;
                offSetLimit.current = offSet;
                let itemList = await loadcontactList();


                const ft = (filterText ?? "").trim().toLowerCase();
                if (ft) {
                    itemList = itemList.filter((x) => (x.name ?? "").toLowerCase().includes(ft));
                }

                return {
                    items: itemList,
                    cursor: String(offSet >= totalRecords ? undefined : currentPage + 1),
                };

            } catch (e) {
                console.error("Error fetching ItemStatus:", e);
                return { items: [] };
            }
        },
    });

    useEffect(() => {
        list.reload();
    }, []);

    // ✅ initial load: auto-select 0th item ONCE
    useEffect(() => {
        if (!didInitialAutoSelectRef.current && list.items.length > 0 && !isEdit) {
            didInitialAutoSelectRef.current = true;

            const first = list.items[0];
            const firstId = String((first as any).id);

            setSelectedKey(firstId);
            setInputValue(`${first.name ?? ""}`);
            onChange(first);
        }
    }, [list.items, isEdit, onChange]);

    // ✅ sync from parent ONLY when user is not actively typing
    useEffect(() => {
        if (isUserEditingRef.current) return;

        if (!selectedContact) {
            setSelectedKey(null);
            setInputValue("");
            return;
        }

        setSelectedKey(String(selectedContact));
        const selected = list.items.find((x) => String((x as any).id) === String(selectedContact));
        setInputValue(`${selected?.name ?? ""}`);
    }, [selectedContact, list.items]);

    const handleInputChange = (val: string) => {
        isUserEditingRef.current = true;

        setInputValue(val);
        list.setFilterText(val);

        // ✅ IMPORTANT: allow deleting text even if something is selected
        // If user input diverges from selected option label, clear the selectedKey
        if (selectedKey) {
            const selected = list.items.find((x) => String((x as any).id) === String(selectedKey));
            const selectedLabel = `${selected?.name ?? ""}`;
            if (val !== selectedLabel) {
                setSelectedKey(null);
            }
        }

        // ✅ goal #4: when cleared -> empty selection
        if (!val.trim()) {
            setSelectedKey(null);
            list.setFilterText(""); // reset filtering
            onChange(null);         // tell parent it is cleared
        }
    };

    const handleSelectionChange = (key: React.Key | null) => {
        isUserEditingRef.current = false;

        const idStr = key ? String(key) : null;
        setSelectedKey(idStr);

        if (!idStr) return;

        const selected = list.items.find((x) => String((x as any).id) === idStr);
        if (selected) {
            setInputValue(`${selected.name ?? ""}`); // show immediately
            onChange(selected);

            // clear filter so next focus shows all values
            list.setFilterText("");
        }
    };

    return (
        <ComboBox
            label={resolvedLabel}
            aria-label={resolvedLabel}
            items={list.items}
            selectedKey={selectedKey}
            inputValue={inputValue}
            loadingState={list.loadingState}
            width={width}
            isDisabled={isEdit}
            isRequired={isRequired}
            errorMessage={errorMessage}
            validationState={validationState}
            onFocus={() => {
                // ✅ goal #2 show all values when user clicks in
                isUserEditingRef.current = true;
                list.setFilterText("");
            }}
            onBlur={() => {
                isUserEditingRef.current = false;
            }}
            onInputChange={handleInputChange}
            onSelectionChange={handleSelectionChange}
            onLoadMore={list.loadMore}
        >
            {(item) => (
                <Item key={(item as any).id} textValue={`${item.name ?? ""}`}>
                    {`${item.name ?? ""}`}
                </Item>
            )}
        </ComboBox>
    );
};

export default ContactDropDown;
